import { useCallback, useEffect } from 'react';

import { usePanoramaOnClick } from '../../PanoramaLibrary/hooks/usePanoramaOnClick';
import { quizMessages } from '../../translates/quizMessages';
import {
  blinkHiddenArea,
  disableHotspot,
  hideHandCursor,
} from '../features/EditItem/components/NonSequencedContent/domain/renderHotspotsApi';
import { useIntlReexport } from './useIntlReexport';
import { useSwitch } from './useSwitch';
import { CameraPosition, IndicatorType, PolygonalArrow, PolygonalArrowBuilder } from '@ael/core-api';
import { FriendlyNameService, HotspotStyle, IPanorama, RenderOptions } from '@ael/core-api';

const HIDDEN_AREA_ARROW_ID = (nr: number) => FriendlyNameService.getFriendlyName(`HiddenAreaArrowId_${nr}`);
const HINT_HOTSPOT = (nr: number) => FriendlyNameService.getFriendlyName(`hiddenareahint_${nr}`);

const CONFIRM_LOCATION_ARROW_ID = FriendlyNameService.getFriendlyName('HiddenAreaWidgetConfirmLocationArrowId');
const CONFIRM_LOCATION_LAYER_ID = FriendlyNameService.getFriendlyName('HiddenAreaWidgetConfirmLocationLayerId');

export const useLocatingHiddenAreaInStudent = (
  panorama: IPanorama | null,
  cameraPosition: CameraPosition | null,
  positions: PolygonalArrow[],
  confirmPosition: (correct: boolean) => void
) => {
  const { formatMessage } = useIntlReexport();

  const { isOn: isOverHiddenArea, turnOn: setOverHiddenArea, turnOff: setOutOfHiddenArea } = useSwitch(false);

  const renderHiddenAreaArrow = useCallback(
    (panorama: IPanorama) => {
      if (panorama) {
        const options: RenderOptions = {
          onClick: () => {
            setOverHiddenArea();
          },
          modifyHotspot: (hotspot) => {
            hideHandCursor(hotspot);
            hotspot.onover = setOverHiddenArea;
            hotspot.onmousedown = setOverHiddenArea;
            hotspot.ondown = setOverHiddenArea;

            hotspot.onmouseup = setOutOfHiddenArea;
            hotspot.onout = setOutOfHiddenArea;
          },
        };

        positions.map((position, index) => {
          // legacy code - BlindMap should be hidden by default
          const hiddenAreaArrow = new PolygonalArrowBuilder(position).setIndicatorType(IndicatorType.Invisible).build();
          panorama.renderArrowToPano(hiddenAreaArrow, HIDDEN_AREA_ARROW_ID(index), options);
          if (cameraPosition) {
            panorama.lookToCameraPosition(cameraPosition);
          }
        });
      }
    },
    [positions, cameraPosition, setOverHiddenArea, setOutOfHiddenArea]
  );

  const showCorrectOrIncorrect = useCallback(() => {
    if (panorama) {
      const mousePosition = panorama._pano.calculateMousePosition();
      const hotspot = panorama._pano.addHotspot(CONFIRM_LOCATION_ARROW_ID);
      hotspot.loadstyle(HotspotStyle.HotspotConfirmClick);
      hotspot.ath = mousePosition.x;
      hotspot.atv = mousePosition.y;
      hotspot.visible = true;
      hotspot.enabled = true;
      hotspot.alpha = 0;
      hotspot.scale = 0;

      const layer = panorama._pano.addLayer(CONFIRM_LOCATION_LAYER_ID);

      const onConfirm = () => {
        panorama.removeArrow(CONFIRM_LOCATION_ARROW_ID);
        panorama.removeLayer(CONFIRM_LOCATION_LAYER_ID);
        if (isOverHiddenArea) {
          panorama.displayCorrectAnswerIndicator(formatMessage(quizMessages.correct), mousePosition);
          confirmPosition(true);
        } else {
          panorama.displayIncorrectAnswerIndicator(formatMessage(quizMessages.tryAgain), mousePosition);
          confirmPosition(false);
        }
      };

      layer.parent = `hotspot[${CONFIRM_LOCATION_ARROW_ID}]`;
      layer.alpha = 1;
      layer.background = false;
      layer.zorder = 99;
      layer.renderer = 'css3d';
      layer.handcursor = true;
      layer.type = 'text';
      layer.align = 'centertop';
      layer.y = -20;
      layer.onclick = onConfirm;
      layer.html = "<div class='confirm-button' style='z-index: 9999'>Confirm</div>";

      panorama._pano.call(`tween(hotspot[${CONFIRM_LOCATION_ARROW_ID}].alpha, 1, 0.3, linear);`);
      panorama._pano.call(`tween(hotspot[${CONFIRM_LOCATION_ARROW_ID}].scale, 1, 0.3, easeoutback);`);
      panorama._pano.call(`tween(layer[${CONFIRM_LOCATION_LAYER_ID}].y, -50, 0.3, easeoutback);`);
    }
  }, [panorama, isOverHiddenArea, formatMessage, confirmPosition]);

  usePanoramaOnClick(panorama, showCorrectOrIncorrect);

  useEffect(() => {
    if (panorama) {
      renderHiddenAreaArrow(panorama);
      return () => {
        positions.forEach((_, index) => panorama.removeArrow(HIDDEN_AREA_ARROW_ID(index)));
        panorama.removeArrow(CONFIRM_LOCATION_ARROW_ID);
        panorama.removeLayer(CONFIRM_LOCATION_LAYER_ID);
      };
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panorama]);

  const displayHint = useCallback(() => {
    panorama?.lookToArrow(positions[0]);
    positions.map((position, index) => {
      const hintHotspotName = HINT_HOTSPOT(index);
      const options: RenderOptions = {
        modifyHotspot: (hotspot) => {
          hideHandCursor(hotspot);
          disableHotspot(hotspot);
          const deleteSelf = () => {
            panorama?.removeArrow(hintHotspotName);
          };
          if (panorama) {
            blinkHiddenArea(panorama, hotspot, deleteSelf);
          }
        },
      };
      panorama?.renderArrowToPano(position, hintHotspotName, options);
    });
  }, [panorama, positions]);

  return {
    renderHiddenAreaArrow,
    displayHint,
  };
};
